import type { PropsWithChildren } from "react";

import type {
  AnimatedSensor,
  SharedValue,
  ValueRotation,
} from "react-native-reanimated";
import Animated, {
  SensorType,
  useAnimatedSensor,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
} from "react-native-reanimated";

import { isWeb } from "@/utils/platform.util";

enum ParallaxConfig {
  MaxOffset = 6,
  TiltRange = 0.3,
}

interface MotionParallaxSensor {
  baseline: SharedValue<null | ValueRotation>;
  sensor: AnimatedSensor<ValueRotation>;
}

// Starts the rotation sensor and creates a shared baseline value.
// Scope to the visible component only -- the sensor stays active and consumes battery.
// Returns null when reduced motion is enabled or on web.
const useMotionParallax = (): MotionParallaxSensor | null => {
  const isReducedMotion = useReducedMotion();
  const isDisabled = isReducedMotion || isWeb();
  // Hook must be called unconditionally; high interval effectively disables polling
  const sensor = useAnimatedSensor(SensorType.ROTATION, {
    interval: isDisabled ? Infinity : "auto",
  });
  // SharedValue because the worklet writes the first reading on the UI thread
  const baseline = useSharedValue<null | ValueRotation>(null);

  if (isDisabled) return null;
  return { baseline, sensor };
};

interface MotionParallaxLayerProps extends PropsWithChildren {
  depth: number;
  // null when useMotionParallax returns null (reduced motion or web)
  motion: MotionParallaxSensor | null;
}

/** Shifts children based on device tilt relative to the initial hold angle. Depth controls magnitude; negative depth moves opposite to tilt. Returns identity style when motion is null (reduced motion or no sensor). */
const MotionParallaxLayer = ({
  children,
  depth,
  motion,
}: MotionParallaxLayerProps) => {
  const style = useAnimatedStyle(() => {
    "worklet";
    if (!motion) return {};
    const { pitch, roll } = motion.sensor.sensor.value;

    if (!motion.baseline.value) {
      // Skip until the sensor produces real data (initial readings are zeros)
      if (pitch === 0 && roll === 0) return {};
      // eslint-disable-next-line no-param-reassign -- Reanimated shared value mutation
      motion.baseline.value = motion.sensor.sensor.value;
      return {};
    }

    const deltaPitch = pitch - motion.baseline.value.pitch;
    const deltaRoll = roll - motion.baseline.value.roll;
    const offset = ParallaxConfig.MaxOffset * depth;
    const range = ParallaxConfig.TiltRange;
    // Clamp delta to [-range, range] then normalize to [-1, 1].
    // Uses Math.max/min instead of lodash/clamp because worklets run on the UI thread.
    const normalizedRoll = Math.max(-range, Math.min(range, deltaRoll)) / range;
    const normalizedPitch =
      Math.max(-range, Math.min(range, deltaPitch)) / range;
    return {
      transform: [
        { translateX: normalizedRoll * offset },
        { translateY: normalizedPitch * offset },
      ],
    };
  });

  return <Animated.View style={style}>{children}</Animated.View>;
};

export type { MotionParallaxSensor };
export { MotionParallaxLayer, useMotionParallax };
